import type { Node } from '@xyflow/react';

import { ActionShowSingleScreenNode } from './ActionNodes/ActionShowSingleScreenNode';
import { ActionStartHomeTimerNode } from './ActionNodes/ActionStartHomeTimerNode.tsx';
import { ActionSwitchLanguageNode } from './ActionNodes/ActionSwitchLanguageNode';
import { SensorButtonNode } from './SensorNodes/SensorButtonNode';
import { SensorBuzzerNode } from './SensorNodes/SensorBuzzerNode.tsx';
import { SensorNFCNodeAll } from './SensorNodes/SensorNFCNodeAll';
import { SensorNFCNodeSpecific } from './SensorNodes/SensorNFCNodeSpecific';
import { ActionToggleLEDNode } from './ActionNodes/ActionToggleLEDNode';
import { SensorTouchNode } from './SensorNodes/SensorTouchNode';
import ActionDispatchCustomEventNode from './ActionNodes/ActionDispatchCustomEvent';
import { SupportedColors } from '../SettingsSidebar/types';
import { ActionTurnOnLEDNode } from './ActionNodes/ActionTurnOnLEDNode';
import { ActionTurnOffLEDNode } from './ActionNodes/ActionTurnOffLEDNode';
import { ActionCustomCodeNode } from './ActionNodes/ActionCustomCodeNode';

/** Gemeinsamer Teil aller Nodes */
type BaseNodeData<TOptions> = {
  label: string;
  options: TOptions;
  isValid: boolean;
};

// NFC Sensor Block
export type SensorNFCAllNodeType = Node<
  BaseNodeData<{
    channelSelective: boolean;
    splitScreen: boolean;
    splitScreenChannels: {
      channelOne: string | null;
      channelTwo: string | null;
    };
    combiScreen: boolean;
    combiScreenChannels: {
      channelOne: string | null;
      channelTwo: string | null;
    };
  }>,
  'sensor-nfcAll'
>;

// NFC Specific Trihow Sensor Block
export type SensorTrihowSpecificNodeType = Node<
  BaseNodeData<{
    trihow: { triggerId: string; name: string }[] | null;
    channel: string | null;
    language: string | null;
  }>,
  'sensor-nfcSpecific'
>;

// Button Sensor Block
export type SensorButtonNodeType = Node<
  BaseNodeData<{
    buttons: { triggerId: string; name: string }[] | null;
    language: string | null;
    subtype: 'regular';
  }>,
  'sensor-button'
>;

// Buzzer Sensor Block
export type SensorBuzzerNodeType = Node<
  BaseNodeData<{
    buzzers: { triggerId: string; name: string }[] | null;
    language: string | null;
    subtype: 'regular';
  }>,
  'sensor-buzzer'
>;

// Touch Sensor Block
export type SensorTouchNodeType = Node<
  BaseNodeData<{
    language: string | null;
  }>,
  'sensor-touch'
>;

// showSingleScreen Action
export type ActionShowSingleScreenNodeType = Node<
  BaseNodeData<{
    genericKey: string | null;
    languageSelective: boolean;
    loopVideo: boolean | null;
    renderOnScreen: number;
    frameType: string;
  }>,
  'action-showSingleScreen'
>;

// startHometimer Action
export type ActionStartHometimerNodeType = Node<
  BaseNodeData<{}>,
  'action-startHometimer'
>;

// switch Language Action
export type ActionSwitchLanguageNodeType = Node<
  BaseNodeData<{
    targetLanguage: string | null;
  }>,
  'action-switchLanguage'
>;

// dispatchCustomEvent Action
export type ActionDispatchCustomEventNodeType = Node<
  BaseNodeData<{
    eventName: string;
    eventData: string;
  }>,
  'action-dispatchCustomEvent'
>;

// LED Identifier Options given through Hardware
export type LEDIdentifierOptions = 'A' | 'B' | 'C' | 'D' | 'E' | 'X';

// toggleLED Action
export type ActionToggleLEDNodeType = Node<
  BaseNodeData<{
    leds:
      | {
          channel: string;
          identifier: LEDIdentifierOptions[] | null;
          name: string | null;
        }[]
      | null;
    ramp: boolean;
    rampOptions: {
      brightness: number;
      colour: SupportedColors;
      duration: number;
    };
    pulse: boolean;
    pulseOptions: {
      brightnessStateOne: number;
      brightnessStateTwo: number;
      colourStateOne: SupportedColors;
      colourStateTwo: SupportedColors;
      rampDuration: number;
    };
    off: {
      rampDownDuration: number | 0;
    };
  }>,
  'action-toggleLED'
>;

// turn on LED Action
export type ActionTurnOnLEDNodeType = Node<
  BaseNodeData<{
    leds:
      | {
          channel: string;
          identifier: LEDIdentifierOptions[] | null;
          name: string | null;
        }[]
      | null;
    ramp: boolean;
    rampOptions: {
      brightness: number;
      colour: SupportedColors;
      duration: number;
    };
    pulse: boolean;
    pulseOptions: {
      brightnessStateOne: number;
      brightnessStateTwo: number;
      colourStateOne: SupportedColors;
      colourStateTwo: SupportedColors;
      rampDuration: number;
    };
  }>,
  'action-turnOnLED'
>;

// turn off LED Action
export type ActionTurnOffLEDNodeType = Node<
  BaseNodeData<{
    leds:
      | {
          channel: string;
          identifier: LEDIdentifierOptions[] | null;
          name: string | null;
        }[]
      | null;
    rampDownDuration: number | 0;
  }>,
  'action-turnOffLED'
>;

// Custom Event Dispatch Action
// Custom Code Execution Action
export type ActionCustomCodeNodeType = Node<
  BaseNodeData<{
    customCode: string;
  }>,
  'action-customCode'
>;

// used for ALL Flow Nodes construction
export type SensorNodeType =
  | SensorNFCAllNodeType
  | SensorTrihowSpecificNodeType
  | SensorButtonNodeType
  | SensorBuzzerNodeType
  | SensorTouchNodeType;

// used for ALL Flow Nodes construction
export type ActionNodeType =
  | ActionShowSingleScreenNodeType
  | ActionStartHometimerNodeType
  | ActionSwitchLanguageNodeType
  | ActionToggleLEDNodeType
  | ActionTurnOnLEDNodeType
  | ActionTurnOffLEDNodeType
  | ActionCustomCodeNodeType
  | ActionDispatchCustomEventNodeType;

// type for all custom nodes
export type AllFlowNodes = SensorNodeType | ActionNodeType;

// used in the defaultData.ts file for initializing nodedata on drop in canvas
export type initialNodeDataType = {
  'sensor-nfcAll': SensorNFCAllNodeType['data'];
  'sensor-nfcSpecific': SensorTrihowSpecificNodeType['data'];
  'sensor-button': SensorButtonNodeType['data'];
  'sensor-buzzer': SensorBuzzerNodeType['data'];
  'sensor-touch': SensorTouchNodeType['data'];
  'action-showSingleScreen': ActionShowSingleScreenNodeType['data'];
  'action-startHometimer': ActionStartHometimerNodeType['data'];
  'action-switchLanguage': ActionSwitchLanguageNodeType['data'];
  'action-toggleLED': ActionToggleLEDNodeType['data'];
  'action-turnOnLED': ActionTurnOnLEDNodeType['data'];
  'action-turnOffLED': ActionTurnOffLEDNodeType['data'];
  'action-dispatchCustomEvent': ActionDispatchCustomEventNodeType['data'];
  'action-customCode': ActionCustomCodeNodeType['data'];
};

// used to map node types to their components for react flow use in canvas
export const nodeTypeComponents = {
  'sensor-nfcAll': SensorNFCNodeAll,
  'sensor-nfcSpecific': SensorNFCNodeSpecific,
  'sensor-button': SensorButtonNode,
  'sensor-buzzer': SensorBuzzerNode,
  'sensor-touch': SensorTouchNode,
  'action-showSingleScreen': ActionShowSingleScreenNode,
  'action-switchLanguage': ActionSwitchLanguageNode,
  'action-startHometimer': ActionStartHomeTimerNode,
  'action-toggleLED': ActionToggleLEDNode,
  'action-turnOnLED': ActionTurnOnLEDNode,
  'action-turnOffLED': ActionTurnOffLEDNode,
  'action-dispatchCustomEvent': ActionDispatchCustomEventNode,
  'action-customCode': ActionCustomCodeNode,
};

// remove sensor-nfcAll from sensorNodeComponents, so that it doesn't show up in BlocksSidebar since it maps the entries to draggable components
export const sensorNodeComponents = Object.fromEntries(
  Object.entries(nodeTypeComponents).filter(
    ([key]) => key.startsWith('sensor-') && !key.startsWith('sensor-nfcAll'),
  ),
);

export const actionNodeComponents = Object.fromEntries(
  Object.entries(nodeTypeComponents).filter(([key]) =>
    key.startsWith('action-'),
  ),
);

// used for the settings sidbar to map reactflow node types to their typescript types
export type NodeTypeMap = {
  'sensor-nfcAll': SensorNFCAllNodeType;
  'sensor-nfcSpecific': SensorTrihowSpecificNodeType;
  'sensor-button': SensorButtonNodeType;
  'sensor-buzzer': SensorBuzzerNodeType;
  'sensor-touch': SensorTouchNodeType;
  'action-showSingleScreen': ActionShowSingleScreenNodeType;
  'action-startHometimer': ActionStartHometimerNodeType;
  'action-switchLanguage': ActionSwitchLanguageNodeType;
  'action-toggleLED': ActionToggleLEDNodeType;
  'action-turnOnLED': ActionTurnOnLEDNodeType;
  'action-turnOffLED': ActionTurnOffLEDNodeType;
  'action-dispatchCustomEvent': ActionDispatchCustomEventNodeType;
  'action-customCode': ActionCustomCodeNodeType;
};
